import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { useRunConnectorOperationMutation } from '../../../store/integrations/integrations'
import { type ConnectorResult } from '../../../models/AccountOpening'
import { type AccountOpeningDescriptor, type AccountOpeningOperationStep } from '../../../models/AccountOpeningDescriptor'
import {
  blockingIssues,
  operationSucceeded,
  statusLabel,
  statusLabelsOf,
  statusTone,
  submissionOutcome,
  type SubmissionOutcome,
} from '../domain/connectorResult'
import { ConnectorIssues, ErrorText, HintText, StatusChip, SuccessLine } from './flowPrimitives'
import { connectorPayload } from '../domain/parties'
import { withSharedAnswers } from '../domain/collections'
import { operationCopy, WIZARD_ERRORS } from '../descriptor/stepCopy'
import { asOfText } from '../domain/asOf'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { type WizardAccount, type WizardContext, type WizardParty } from '../domain/types'
import styles from '../AccountOpeningWizard.module.scss'

interface OperationStepProps {
  ctx: WizardContext
  step: AccountOpeningOperationStep
  accounts: WizardAccount[]
  parties: WizardParty[]
  descriptor: AccountOpeningDescriptor | undefined
  result: SubmissionOutcome | null
  lastRunAt?: string
  committed: boolean
  ready: boolean
  onResult: (outcome: SubmissionOutcome) => Promise<void> | void
  onRunFinished?: (outcome: SubmissionOutcome | null) => void
}

export const OperationStep: FC<OperationStepProps> = ({
  ctx,
  step,
  accounts,
  parties,
  descriptor,
  result,
  lastRunAt,
  committed,
  ready,
  onResult,
  onRunFinished,
}) => {
  const [runOperation, { isLoading }] = useRunConnectorOperationMutation()
  const [callError, setCallError] = useState<string | null>(null)
  const [isFinishing, setIsFinishing] = useState(false)

  const copy = operationCopy(step)
  const accountTimeZone = useAccountTimezone()
  const lastRun = asOfText(copy.lastRun, lastRunAt, accountTimeZone)
  const succeeded = operationSucceeded(result)
  const issues = blockingIssues(result?.errors ?? [])
  const handoffUrl = result?.handoffUrl ?? null
  const running = isLoading || isFinishing
  const locked = succeeded || committed

  const requestOutcome = async (): Promise<SubmissionOutcome | null> => {
    try {
      const shared = withSharedAnswers(descriptor?.steps ?? [], accounts, parties)
      const response = (await runOperation({
        accountId: ctx.accountId,
        connector: ctx.connector,
        operation: step.operation,
        body: { payload: connectorPayload(ctx, shared.accounts, shared.parties) },
      }).unwrap()) as { data: ConnectorResult }
      return submissionOutcome(response.data)
    } catch {
      return null
    }
  }

  const handleRun = async () => {
    if (isFinishing) {
      return
    }
    setIsFinishing(true)
    setCallError(null)
    const outcome = await requestOutcome()
    onRunFinished?.(outcome)
    try {
      if (!outcome) {
        setCallError(WIZARD_ERRORS.operationFailed)
        return
      }
      if (step.opensResultLink && operationSucceeded(outcome) && outcome.handoffUrl) {
        window.open(outcome.handoffUrl, '_blank', 'noopener,noreferrer')
      }
      await onResult(outcome)
      if (!outcome.ok && outcome.errors.length === 0) {
        setCallError(WIZARD_ERRORS.operationRefused)
      }
    } catch {
      setCallError(WIZARD_ERRORS.operationFailed)
    } finally {
      setIsFinishing(false)
    }
  }

  const showAction = step.irreversible ? !succeeded : !locked

  return (
    <div className={classNames('rounded-3 p-3 d-flex flex-column zocks-gap-1', styles.stepCard)}>
      <p className='paragraph-medium mb-0'>{copy.body}</p>

      {ready || succeeded ? null : <ErrorText>{copy.notReady}</ErrorText>}

      {succeeded ? <SuccessLine>{copy.success}</SuccessLine> : null}

      {showAction ? (
        <Button
          variant='primary'
          className='align-self-start d-flex align-items-center zocks-gap-0.5'
          onClick={handleRun}
          loading={running}
          disabled={running || locked || !ready || accounts.length === 0}
        >
          {running ? null : step.opensResultLink ? <i className='bi bi-send' /> : null}
          {running ? copy.pending : result && copy.actionAgain ? copy.actionAgain : copy.action}
        </Button>
      ) : null}

      {running ? <HintText>{copy.hint}</HintText> : null}

      {succeeded && (result?.referenceId || result?.statusCode) ? (
        <div className='d-flex align-items-center zocks-gap-1 flex-wrap'>
          {result.referenceId ? (
            <span className='paragraph-small zocks-text-gray-600'>
              {copy.referenceLabel} <code>{result.referenceId}</code>
            </span>
          ) : null}
          {result.statusCode ? (
            <StatusChip tone={statusTone(descriptor, result.statusCode)}>{statusLabel(statusLabelsOf(descriptor), result.statusCode)}</StatusChip>
          ) : null}
        </div>
      ) : null}

      {succeeded && step.opensResultLink ? (
        handoffUrl ? (
          <a className='paragraph-medium align-self-start' href={handoffUrl} target='_blank' rel='noopener noreferrer'>
            {copy.linkLabel}
          </a>
        ) : (
          <span className='paragraph-medium zocks-text-gray-600'>{copy.linkMissing}</span>
        )
      ) : null}

      {!succeeded && copy.warning ? (
        <div className='d-flex align-items-center zocks-gap-0.5 zocks-text-warning-text'>
          <i className='bi bi-exclamation-circle' />
          <span className='paragraph-medium'>{copy.warning}</span>
        </div>
      ) : null}

      <ConnectorIssues heading={copy.issuesHeading} errors={issues} />

      {callError ? <ErrorText>{callError}</ErrorText> : null}

      {lastRun ? <HintText className='text-end'>{lastRun}</HintText> : null}
    </div>
  )
}
